import * as React from 'react';
import * as wijmo from '@grapecity/wijmo';

import * as wjChart from '@grapecity/wijmo.react.chart';
import * as wjGrid from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridColumn } from '@grapecity/wijmo.react.grid';

import { Col, Row, Container, Form } from 'react-bootstrap';
import { useEffect, useState, useRef } from 'react';

import { toInt, toFloat, calcRate } from 'utils/numberUtils';
import { isBeforeThisMonth } from 'utils/dateUtils';
import './Analysis.css';

// 分析（相談件数）（昨対比）
function AnalysisSoudanCurrentBefore(props) {
  const gridRef = useRef(null);

  // 見積依頼リスト
  const quoteList = props.quoteList;
  // 選択中の期
  const selectedPeriod = props.selectedPeriod;

  // 集計範囲の切り替え状態
  const [rangeType, setRangeType] = useState('all');

  // 前期のリピート件数
  const [beforeRepeatCount, setBeforeRepeatCount] = useState(0);
  // 前期の新規件数
  const [beforeNewCount, setBeforeNewCount] = useState(0);
  // 今期のリピート件数
  const [currentRepeatCount, setCurrentRepeatCount] = useState(0);
  // 今期の新規件数
  const [currentNewCount, setCurrentNewCount] = useState(0);

  // 集計対象データを切り替え
  const getFilteredCollection = () => {
    if (rangeType === 'untilThisMonth') {
      return quoteList.sourceCollection.filter((item) =>
        isBeforeThisMonth(item.MONTH, selectedPeriod)
      );
    }
    return quoteList.sourceCollection;
  };

  useEffect(() => {
    if (gridRef.current) {
      updateFooter(gridRef.current.control);
    }

    // 集計単位（全期間・前月まで）に合わせてフィルタ
    let filteredCollection = getFilteredCollection();

    // 前期のリピート・新規件数を計算
    setBeforeRepeatCount(
      filteredCollection.reduce((sum, item) => sum + toInt(item.BEFORE_REPEAT, 0), 0)
    );
    setBeforeNewCount(filteredCollection.reduce((sum, item) => sum + toInt(item.BEFORE_NEW, 0), 0));

    // 今期のリピート・新規件数を計算
    setCurrentRepeatCount(
      filteredCollection.reduce((sum, item) => sum + toInt(item.CURRENT_REPEAT), 0)
    );
    setCurrentNewCount(
      filteredCollection.reduce((sum, item) => sum + toInt(item.CURRENT_NEW, 0), 0)
    );
  }, [quoteList, rangeType]);

  //  グリッドの初期化
  function onInitialized(grid) {
    // ヘッダーを2段にする
    var extraRow = new wjGrid.Row();
    extraRow.allowMerging = true;

    var panel = grid.columnHeaders;
    panel.rows.splice(0, 0, extraRow);

    for (let colIndex = 1; colIndex <= 3; colIndex++) {
      panel.setCellData(0, colIndex, 'ご相談件数');
    }
    for (let colIndex = 4; colIndex <= 6; colIndex++) {
      panel.setCellData(0, colIndex, 'リピート');
    }
    for (let colIndex = 7; colIndex <= 9; colIndex++) {
      panel.setCellData(0, colIndex, '新規');
    }

    // 垂直方向にマージ
    ['MONTH'].forEach(function (binding) {
      let col = grid.getColumn(binding);
      col.allowMerging = true;
      panel.setCellData(0, col.index, col.header);
    });
    // ヘッダーの中央揃え
    grid.formatItem.addHandler((sender, e) => {
      // ヘッダーセルのスタイル設定
      if (e.panel === sender.columnHeaders) {
        e.cell.style.textAlign = 'center';
        e.cell.style.display = 'flex';
        e.cell.style.justifyContent = 'center';
        e.cell.style.alignItems = 'center';
      }

      // フッターセルのスタイル設定
      if (e.panel === sender.columnFooters) {
        const colIndex = e.col;
        const rowIndex = e.row;

        // フッターの1行目の線を太くする
        if (rowIndex === 0) {
          e.cell.style.borderTop = 'double';
        }

        if (rowIndex == 0 && [1, 4, 7].includes(colIndex)) {
          // 前期
          e.cell.style.backgroundColor = '#fce4d6';
        } else if (rowIndex == 0 && [2, 5, 8].includes(colIndex)) {
          // 今期
          e.cell.style.backgroundColor = '#ddebf7';
        } else {
          // 他のセルの背景色をデフォルトに戻す
          e.cell.style.backgroundColor = '';
        }
      }
    });

    // フッターに２段追加
    grid.columnFooters.rows.push(new wjGrid.Row());
    grid.columnFooters.rows.push(new wjGrid.Row());

    // データ変更時に合計更新
    grid.collectionView.collectionChanged.addHandler(updateFooter);

    updateFooter(grid);
  }

  // 合計値を計算してフッターに設定
  function updateFooter(grid) {
    let data = grid?.itemsSource?.items;
    if (!data || data.length === 0) return;

    let panel = grid.columnFooters;

    grid.columns.forEach((col, colIndex) => {
      const binding = col.binding;
      if (binding === 'MONTH') {
        // 月
        panel.setCellData(0, colIndex, '合計');
        panel.setCellData(1, colIndex, '前月まで');
      } else if (binding.includes('STAR_')) {
        // 星表
        const star_off_count = data.reduce((cnt, item) => cnt + (item[binding] == '★' ? 1 : 0), 0);
        const star_on_count = data.reduce((cnt, item) => cnt + (item[binding] == '☆' ? 1 : 0), 0);
        panel.setCellData(0, colIndex, 'ー');
        panel.setCellData(1, colIndex, `${star_on_count}☆${star_off_count}★`);
      } else if (col.binding.includes('BEFORE')) {
        // 前期
        let total = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
        panel.setCellData(0, colIndex, total);

        // 今月までの合計を取得
        let totalThisMonth = data
          .filter((item) => isBeforeThisMonth(item['MONTH'], selectedPeriod))
          .reduce((sum, item) => sum + (item[binding] || 0), 0);

        panel.setCellData(1, colIndex, totalThisMonth);

        // 今期
      } else {
        let total = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
        panel.setCellData(0, colIndex, total);

        // 前期の今月までの合計を取得して上昇率を計算
        let beforeThisMonth = data
          .filter((item) => isBeforeThisMonth(item['MONTH'], selectedPeriod))
          .reduce((sum, item) => sum + (item[binding.replace('CURRENT', 'BEFORE')] || 0), 0);
        let upRate = calcRate(total, beforeThisMonth, 4);
        panel.setCellData(1, colIndex, wijmo.format('{val:p1}', { val: upRate }));
      }
    });
  }

  return (
    <Container fluid className="p-0">
      <Row className="mt-2">
        <Col className="pl-3" md="5">
          <FlexGrid
            ref={gridRef}
            isReadOnly={true}
            allowMerging="ColumnHeaders"
            allowSorting={false}
            allowDragging={false}
            itemsSource={quoteList}
            headersVisibility={'Column'}
            alternatingRowStep={0}
            initialized={onInitialized}
          >
            <FlexGridColumn
              binding="MONTH"
              header="月"
              allowMerging={true}
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="BEFORE_TOTAL"
              cssClass="before_cell"
              header={`${selectedPeriod - 1}期`}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="CURRENT_TOTAL"
              cssClass="current_cell"
              header={`${selectedPeriod}期`}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="STAR_TOTAL"
              header="星表"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="BEFORE_REPEAT"
              cssClass="before_cell"
              header={`${selectedPeriod - 1}期`}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="CURRENT_REPEAT"
              cssClass="current_cell"
              header={`${selectedPeriod}期`}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="STAR_REPEAT"
              header="星表"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="BEFORE_NEW"
              cssClass="before_cell"
              header={`${selectedPeriod - 1}期`}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="CURRENT_NEW"
              cssClass="current_cell"
              header={`${selectedPeriod}期`}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="STAR_NEW"
              header="星表"
              width={'*'}
              align="center"
            ></FlexGridColumn>
          </FlexGrid>
        </Col>
        <Col className="pl-3" md="5">
          <wjChart.FlexChart
            header="移動年計　ご相談件数"
            itemsSource={quoteList.sourceCollection}
            bindingX="MONTH"
            chartType="Line"
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
          >
            {/* Y軸の最大・最小値を設定 */}
            <wjChart.FlexChartAxis wjProperty="axisY" min={0} />
            {/* X軸にY方向のグリッド線を表示 */}
            <wjChart.FlexChartAxis wjProperty="axisX" majorGrid={true} />
            {/* 凡例の位置を下に配置 */}
            <wjChart.FlexChartLegend position="Bottom" />
            <wjChart.FlexChartSeries name={`${selectedPeriod - 1}期`} binding="BEFORE_TOTAL" />
            <wjChart.FlexChartSeries name={`${selectedPeriod}期`} binding="CURRENT_TOTAL" />
          </wjChart.FlexChart>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col className="pl-3" md="6">
          <wjChart.FlexChart
            header={`${selectedPeriod - 1}期　リピート・新規（相談件数）【累計】`}
            itemsSource={quoteList.sourceCollection}
            bindingX="MONTH"
            chartType="Line"
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
          >
            {/* Y軸の最大・最小値を設定 */}
            <wjChart.FlexChartAxis wjProperty="axisY" min={0} />
            {/* X軸にY方向のグリッド線を表示 */}
            <wjChart.FlexChartAxis wjProperty="axisX" majorGrid={true} />
            {/* 凡例の位置を下に配置 */}
            <wjChart.FlexChartLegend position="Bottom" />
            <wjChart.FlexChartSeries name={`リピート`} binding="BEFORE_REPEAT" />
            <wjChart.FlexChartSeries name={`新規`} binding="BEFORE_NEW" />
          </wjChart.FlexChart>
        </Col>
        <Col className="pl-3" md="6">
          <wjChart.FlexChart
            header={`${selectedPeriod}期　リピート・新規（相談件数）【累計】`}
            itemsSource={quoteList.sourceCollection}
            bindingX="MONTH"
            chartType="Line"
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
          >
            {/* Y軸の最大・最小値を設定 */}
            <wjChart.FlexChartAxis wjProperty="axisY" min={0} />
            {/* X軸にY方向のグリッド線を表示 */}
            <wjChart.FlexChartAxis wjProperty="axisX" majorGrid={true} />
            {/* 凡例の位置を下に配置 */}
            <wjChart.FlexChartLegend position="Bottom" />
            <wjChart.FlexChartSeries name={`リピート`} binding="CURRENT_REPEAT" />
            <wjChart.FlexChartSeries name={`新規`} binding="CURRENT_NEW" />
          </wjChart.FlexChart>
        </Col>
      </Row>
      {/* 集計範囲切り替え*/}
      <Row>
        <Col md="3">
          <div style={{ width: '120px' }}>
            <label className="item-header-label">集計範囲</label>
            <Form.Control
              as="select"
              value={rangeType}
              onChange={(e) => setRangeType(e.target.value)}
            >
              <option value="all">全期間</option>
              <option value="untilThisMonth">前月まで</option>
            </Form.Control>
          </div>
        </Col>
      </Row>
      <Row className="mt-2">
        {/* 前期のリピート・新規件数の円グラフ */}
        <Col className="pl-3" md="6">
          <wjChart.FlexPie
            header={`${selectedPeriod - 1}期　リピート・新規（相談件数）【累計】`}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            itemsSource={[
              {
                category: 'リピート',
                value: beforeRepeatCount,
                rate: beforeRepeatCount / (beforeRepeatCount + beforeNewCount),
              },
              {
                category: '新規',
                value: beforeNewCount,
                rate: beforeNewCount / (beforeRepeatCount + beforeNewCount),
              },
            ]}
            bindingName="category"
            binding="value"
            startAngle={90}
          >
            <wjChart.FlexPieDataLabel content="{name}({rate:p1})" position="Center" />
          </wjChart.FlexPie>
        </Col>
        {/* 今期のリピート・新規件数の円グラフ */}
        <Col className="pl-3" md="6">
          <wjChart.FlexPie
            header={`${selectedPeriod}期　リピート・新規（相談件数）【累計】`}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            itemsSource={[
              {
                category: 'リピート',
                value: currentRepeatCount,
                rate: currentRepeatCount / (currentRepeatCount + currentNewCount),
              },
              {
                category: '新規',
                value: currentNewCount,
                rate: currentNewCount / (currentRepeatCount + currentNewCount),
              },
            ]}
            bindingName="category"
            binding="value"
            startAngle={90}
          >
            <wjChart.FlexPieDataLabel content="{name}({rate:p1})" position="Center" />
          </wjChart.FlexPie>
        </Col>
      </Row>
    </Container>
  );
}
export default AnalysisSoudanCurrentBefore;
